import API from '../../api.js';
import * as Yup from 'yup';

export const updateM3UProfile = async (playlistId, submitValues) => {
  await API.updateM3UProfile(playlistId, submitValues);
};

export const addM3UProfile = async (playlistId, submitValues) => {
  await API.addM3UProfile(playlistId, submitValues);
};

export const deleteM3UProfile = async (playlistId, id) => {
  await API.deleteM3UProfile(playlistId, id);
};

const queryStreams = async (params) => {
  return await API.queryStreams(params);
};

export const getDetectedMode = (storedMode, profile, m3u) => {
  if (storedMode) {
    return storedMode;
  } else if (
    profile?.search_pattern &&
    profile.search_pattern === `${m3u?.username}/${m3u?.password}`
  ) {
    return 'simple';
  } else if (profile?.search_pattern) {
    return 'advanced';
  }
  return 'simple';
};

export const applyRegex = (input, pattern, replacer) => {
  if (!pattern || !input) return input;
  try {
    const regex = new RegExp(pattern, 'g');
    return input.replace(regex, replacer);
  } catch {
    return input;
  }
};

/**
 * Splits `input` into an array of { text, matched } segments using `pattern`.
 * Returns null if the pattern is empty or the input is empty.
 */
export const splitByPattern = (input, pattern) => {
  if (!pattern || !input) return null;
  try {
    const regex = new RegExp(pattern, 'g');
    const segments = [];
    let lastIndex = 0;
    let m;
    while ((m = regex.exec(input)) !== null) {
      if (m.index > lastIndex) {
        segments.push({
          text: input.slice(lastIndex, m.index),
          matched: false,
        });
      }
      segments.push({ text: m[0], matched: true });
      lastIndex = m.index + m[0].length;
      if (m[0].length === 0) regex.lastIndex++;
    }
    if (lastIndex < input.length) {
      segments.push({ text: input.slice(lastIndex), matched: false });
    }
    return segments;
  } catch {
    return null;
  }
};

export const buildProfileSchema = (isDefaultProfile, isXC) => {
  return Yup.object({
    name: Yup.string().required('Name is required'),
    search_pattern: Yup.string().when([], {
      is: () => !isDefaultProfile && !isXC,
      then: (schema) => schema.required('Search pattern is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    replace_pattern: Yup.string().when([], {
      is: () => !isDefaultProfile && !isXC,
      then: (schema) => schema.required('Replace pattern is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    notes: Yup.string(),
  });
};

export const fetchFirstStreamUrl = async (m3uId) => {
  const params = new URLSearchParams();
  params.append('page', 1);
  params.append('page_size', 1);
  params.append('m3u_account', m3uId);
  const response = await queryStreams(params);
  return response?.results?.[0]?.url ?? null;
};

export const validateXcSimple = (newUsername, newPassword) => {
  const errs = {};
  if (!newUsername.trim()) errs.newUsername = 'New username is required';
  if (!newPassword.trim()) errs.newPassword = 'New password is required';
  return errs;
};

export const prepareExpDate = (expDateValue, isXC) => {
  if (isXC) return undefined;
  if (expDateValue instanceof Date) return expDateValue.toISOString();
  return expDateValue || null;
};

/**
 * Resolve the expiration picker value for a profile editor.
 * For the default profile, prefer the parent account form's unsaved pendingExpDate
 * when provided (including null = cleared). Otherwise use the profile's saved exp_date.
 * @param {{ is_default?: boolean, exp_date?: string|null }|null|undefined} profile
 * @param {Date|string|null|undefined} pendingExpDate undefined means "not provided"
 * @returns {Date|null}
 */
export const resolveProfileExpDate = (profile, pendingExpDate) => {
  if (profile?.is_default && pendingExpDate !== undefined) {
    if (pendingExpDate instanceof Date) {
      return Number.isNaN(pendingExpDate.getTime())
        ? null
        : new Date(pendingExpDate.getTime());
    }
    if (!pendingExpDate) return null;
    const parsed = new Date(pendingExpDate);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (!profile?.exp_date) return null;
  const parsed = new Date(profile.exp_date);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const applyXcSimplePatterns = (
  values,
  m3u,
  newUsername,
  newPassword
) => {
  return {
    ...values,
    search_pattern: `${m3u?.username || ''}/${m3u?.password || ''}`,
    replace_pattern: `${newUsername.trim()}/${newPassword.trim()}`,
  };
};

export const buildSubmitValues = (
  values,
  profile,
  isDefaultProfile,
  isXC,
  xcMode
) => {
  if (isDefaultProfile) {
    return {
      name: values.name,
      search_pattern: values.search_pattern || '',
      replace_pattern: values.replace_pattern || '',
      custom_properties: {
        ...(profile?.custom_properties || {}),
        notes: values.notes || '',
      },
    };
  }
  return {
    name: values.name,
    max_streams: values.max_streams,
    search_pattern: values.search_pattern,
    replace_pattern: values.replace_pattern,
    custom_properties: {
      ...(profile?.custom_properties || {}),
      notes: values.notes || '',
      ...(isXC ? { xcMode } : {}),
    },
  };
};
